import React, { useEffect, useRef, useState } from 'react';
import { Mail, ChevronDown, CheckCircle2, Loader2 } from 'lucide-react';
import { useEmailSequences, SequenceSummary } from '../../hooks/useEmailSequences';

interface Props {
  companyId: string | undefined;
  leadId: string;
}

// Dropdown button that lists active sequences and lets the user enroll a
// lead in one. Enrolled state is transient — refetched via hook after each
// call, so re-opening the menu shows fresh sequence counts.
export const EnrollInSequenceMenu: React.FC<Props> = ({ companyId, leadId }) => {
  const { sequences, enrollLead, loading: sequencesLoading } = useEmailSequences(companyId);
  const [open, setOpen] = useState(false);
  const [enrolling, setEnrolling] = useState<Record<string, boolean>>({});
  const [lastEnrolled, setLastEnrolled] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeSequences = sequences.filter(
    (s) => s.status === 'active'
  ) as SequenceSummary[];

  const handleEnroll = async (seq: SequenceSummary) => {
    setEnrolling((prev) => ({ ...prev, [seq.sequenceId]: true }));
    try {
      await enrollLead(seq.sequenceId, leadId);
      setLastEnrolled(seq.sequenceId);
      setTimeout(() => setOpen(false), 800);
    } catch (err) {
      console.error('[EnrollInSequenceMenu] enroll failed', err);
    } finally {
      setEnrolling((prev) => ({ ...prev, [seq.sequenceId]: false }));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-[11px] inline-flex items-center gap-1.5"
      >
        <Mail className="w-3 h-3" /> Enroll
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-700">
            Active sequences
          </div>
          {sequencesLoading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          )}
          {!sequencesLoading && activeSequences.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              No active sequences. Create one on /leads/email.
            </div>
          )}
          {activeSequences.map((seq) => {
            const isBusy = !!enrolling[seq.sequenceId];
            const justDone = lastEnrolled === seq.sequenceId;
            return (
              <button
                key={seq.sequenceId}
                type="button"
                onClick={() => void handleEnroll(seq)}
                disabled={isBusy || justDone}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-700/50 disabled:opacity-50 flex items-center justify-between gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{seq.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {seq.stepCount} step{seq.stepCount !== 1 ? 's' : ''} ·{' '}
                    {seq.enrolledCount} enrolled
                  </div>
                </div>
                {isBusy ? (
                  <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                ) : justDone ? (
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnrollInSequenceMenu;
